/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 파일 드래그와 관련된 이벤트를 처리한다.
 *
 * @file /src/rootin/events/FileDrag.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 26.
 */
import { Editor } from '@rootin/core/Editor';
import { Block } from '@rootin/base/Block';
import { BlockClass } from '@rootin/base/Blocks';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';

export class FileDrag {
    /**
     * 파일드래그 오버레이가 활성화된 블록
     */
    #dragOverlayBlock: Block | null = null;

    /**
     * 에디터의 파일 드래그와 관련된 이벤트를 처리한다.
     *
     * @param editor - 이벤트를 정의할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 이벤트 리스너를 바인딩한다.
     *
     * @param $container - 이벤트를 받을 컨테이너 DOM
     */
    listen($container: Dom): void {
        // 파일드래그가 진입하였을 때 이벤트를 처리한다.
        $container.on('dragenter', (_, e) => {
            if (e.dataTransfer?.types.includes('Files') !== true) {
                return;
            }

            const fileType = e.dataTransfer.items[0]?.type ?? '';
            const plan = this.#resolveFileDropTarget(e.target, fileType);
            this.#setDragOverlay(plan === null ? null : plan.kind === 'direct' ? plan.block : this.editor.root);
        });

        // 파일 드래그가 에디터에서 완전히 벗어났을 때(하위 요소로 옮겨간 게 아니라) 이벤트를 처리한다.
        $container.on('dragleave', (_, e) => {
            if (e.dataTransfer?.types.includes('Files') !== true) {
                return;
            }
            if ($container.getEl().contains(e.relatedTarget as Node) === true) {
                return;
            }
            this.#setDragOverlay(null);
        });

        // 파일이 실제로 드랍되었을 때 이벤트를 처리한다.
        $container.on('drop', (_, e) => {
            if (e.dataTransfer?.types.includes('Files') !== true) {
                return;
            }
            e.preventDefault();
            this.#setDragOverlay(null);

            const file = e.dataTransfer.files[0] ?? null;
            if (file === null) {
                return;
            }

            const plan = this.#resolveFileDropTarget(e.target, file.type);
            if (plan === null) {
                return;
            }

            if (plan.kind === 'direct') {
                void plan.block.onFileDrop(file);
                return;
            }

            void this.#insertBlockForFileDrop(plan.blockClass, e.target, file);
        });
    }

    /**
     * 파일드래그를 받을 블록을 찾는다.
     *
     * @param target - 이벤트 대상
     * @returns 찾은 블록
     */
    #resolveDropBlock(target: EventTarget | null): Block | null {
        const $block = Html.el(target as EventTarget)?.getParents('rootin-block');
        return ($block?.getData('block') as Block) ?? null;
    }

    /**
     * 파일드래그를 받을 블록을 결정한다.
     *
     * @param target - 이벤트 대상
     * @param fileType - 판정할 파일의 MIME 타입
     * @returns 직접 받는 블록이 있으면 `{kind:'direct', block}`, 새로 추가해서라도 받아줄
     *          블록타입이 있으면 `{kind:'insert', blockClass}`, 아무도 안 받으면 `null`
     */
    #resolveFileDropTarget(
        target: EventTarget | null,
        fileType: string
    ): { kind: 'direct'; block: Block } | { kind: 'insert'; blockClass: BlockClass } | null {
        const targetBlock = this.#resolveDropBlock(target);
        console.log('#resolveFileDropTarget', targetBlock?.getType() ?? null);
        if (targetBlock !== null && (targetBlock.constructor as typeof Block).acceptFileDrop(fileType) !== false) {
            return { kind: 'direct', block: targetBlock };
        }

        for (const type of this.editor.root.body.blocks) {
            const blockClass = this.editor.getBlockClass(type);
            if (blockClass?.acceptFileDrop(fileType) === 'insert') {
                return { kind: 'insert', blockClass };
            }
        }

        return null;
    }

    /**
     * 파일드래그 오버레이를 갱신한다.
     *
     * @param target - 오버레이를 표시할 블록
     */
    #setDragOverlay(target: Block | null): void {
        if (this.#dragOverlayBlock === target) {
            return;
        }

        this.#dragOverlayBlock?.$getBlock().removeClass('dragenter');
        if (target !== null) {
            target.$getBlock().addClass('dragenter');
        }
        this.#dragOverlayBlock = target;
    }

    /**
     * 파일을 드롭받을 새 블록을 만들어 파일드롭을 처리한다.
     *
     * @param blockClass - 추가할 블록 클래스
     * @param target - 드롭 지점
     * @param file - 드롭된 파일
     */
    async #insertBlockForFileDrop(blockClass: BlockClass, target: EventTarget | null, file: File): Promise<void> {
        const anchorBlock = this.#resolveDropBlock(target) ?? this.editor.focus.resolveContainer(target).at(0) ?? null;
        const container = anchorBlock?.getParent() ?? this.editor.root.body;
        const before =
            anchorBlock !== null && anchorBlock.getParent() === container
                ? anchorBlock.$getBlock().getEl().nextElementSibling
                : null;

        const block = blockClass.create(this.editor);
        await container.insertBlock(block, before);
        await block.onFileDrop(file);
    }
}
